import React, { useState, useEffect, useRef, useCallback, memo } from "react";
import { useNavigate } from "react-router-dom";
import getSymbolFromCurrency from 'currency-symbol-map';
import { FaShoppingCart, FaHeart, FaEye, FaAngleLeft, FaAngleRight, FaCrown, FaStar, FaBoxOpen } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import Logo from "../Assest/images/logo.png";
import Loader from "./Loader";
import axios from "axios";
import { useCurrency } from "./CurrencyContext";

// Memoized Product Card component
const ProductCard = memo(({ product, isMobile, onShopNow }) => {
  const [isHovered, setIsHovered] = useState(false);
  const { rate, currency } = useCurrency();
  
  // Force conversion of price to a number and apply the exchange rate
  const productPrice = parseFloat(product.price) || 0;
  const convertedPrice = productPrice * rate;
  
  // Get the correct currency symbol (with special case for SAR)
  const currencySymbol = currency === "SAR" ? "﷼" : getSymbolFromCurrency(currency) || currency;
  
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.4 }}
      className="group relative"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="h-full rounded-xl overflow-hidden bg-white border border-gray-200 shadow hover:shadow-xl transition-all duration-300">
        <div className="relative overflow-hidden aspect-square">
          <motion.img
            src={`${process.env.REACT_APP_API_URI}/uploads/${product.image}`}
            alt={product.product_name}
            className="object-cover w-full h-full transition-transform duration-500"
            animate={{ 
              scale: isHovered ? 1.05 : 1,
            }}
            transition={{ duration: 0.3 }}
          />
          
          <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent opacity-60"></div>
          
          <div className="absolute top-4 left-4">
            <div className="px-3 py-1 bg-[#f36812] text-white text-xs font-semibold rounded-full shadow-md flex items-center">
              <FaCrown className="mr-1 w-3 h-3" />
              VIP
            </div>
          </div>
          
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ 
              opacity: (isHovered || isMobile) ? 1 : 0,
              y: (isHovered || isMobile) ? 0 : 10
            }}
            transition={{ duration: 0.2 }}
            className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/60 to-transparent"
          >
            <div className="flex justify-center gap-3">
              <button aria-label="Add to favorites" className="w-9 h-9 rounded-full bg-white/80 hover:bg-white flex items-center justify-center text-gray-700 transition-all duration-200 hover:scale-105">
                <FaHeart className="w-4 h-4" />
              </button>
              <button onClick={() => onShopNow(product.brand_id, product.product_id)} aria-label="Quick view" className="w-9 h-9 rounded-full bg-white/80 hover:bg-white flex items-center justify-center text-gray-700 transition-all duration-200 hover:scale-105">
                <FaEye className="w-4 h-4" />
              </button>
              <button 
                onClick={() => onShopNow(product.brand_id, product.product_id)}
                aria-label="Add to cart"
                className="w-9 h-9 rounded-full bg-[#f36812] hover:bg-[#e05a00] flex items-center justify-center text-white shadow-md transition-all duration-200 hover:scale-105"
              >
                <FaShoppingCart className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        </div>
        
        <div className="p-4">
          <h3 className="text-lg font-medium text-gray-800 group-hover:text-[#f36812] transition-colors duration-200">
            {product.product_name}
          </h3>
          
          <div className="mt-2 flex justify-between items-center">
            <p className="text-xl font-bold text-[#f36812]">
              {/* This is the key part - displaying the converted price with correct symbol */}
              {currencySymbol} {convertedPrice.toFixed(2)}
            </p>
            
            <div className="flex items-center gap-0.5">
              {[1, 2, 3, 4, 5].map((star) => (
                <FaStar key={star} className="w-3.5 h-3.5 text-[#f36812]" />
              ))}
            </div>
          </div>
          
          <div className="mt-2 space-y-1">
            <div className="flex items-center text-xs text-gray-600">
              <div className="w-1 h-1 rounded-full bg-[#f36812] mr-2"></div>
              {product.brand_name || "Premium Quality"}
            </div>
            <div className="flex items-center text-xs text-gray-600">
              <div className="w-1 h-1 rounded-full bg-[#f36812] mr-2"></div>
              {product.product_type || "Limited Edition"}
            </div>
          </div>
          
          <button
            onClick={() => {
              window.scrollTo(0, 0); // Scroll to top immediately
              onShopNow(product.brand_id, product.product_id);
            }}
            className="mt-3 w-full py-2.5 bg-[#f36812] hover:bg-[#e05a00] 
              text-white font-medium rounded-lg flex items-center justify-center gap-2 
              transition-all duration-200 hover:shadow-md"
          >
            <FaShoppingCart className="w-4 h-4" />
            Shop Now
          </button>
        </div>
      </div>
    </motion.div>
  );
});
// New CardsLoader component for loading company products
const CardsLoader = () => {
  return (
    <div className="fixed inset-0 bg-white/80 backdrop-blur-sm z-20 flex items-center justify-center">
      <div className="text-center">
        <div className="flex justify-center mb-4">
          <div className="relative w-16 h-16">
            <div className="absolute inset-0 rounded-full border-4 border-t-[#f36812] border-r-[#f36812] border-b-transparent border-l-transparent animate-spin"></div>
            <div className="absolute inset-2 rounded-full border-4 border-t-transparent border-r-transparent border-b-[#ff9259] border-l-[#ff9259] animate-spin animation-delay-150"></div>
          </div>
        </div>
        <div className="text-[#f36812] font-medium text-lg">
          Loading Products
          <span className="inline-block animate-bounce">.</span>
          <span className="inline-block animate-bounce animation-delay-150">.</span>
          <span className="inline-block animate-bounce animation-delay-300">.</span>
        </div>
      </div>
    </div>
  );
};

// No Products Found component
const NoProductsFound = ({ companyName }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="w-full py-16 flex flex-col items-center justify-center"
    >
      <FaBoxOpen className="w-16 h-16 text-gray-300 mb-4" />
      <h3 className="text-xl font-medium text-gray-700 mb-2">No Products Found</h3>
      <p className="text-gray-500 text-center max-w-md">
        {companyName ? `${companyName} doesn't have any products available at the moment.` : 'No products available at the moment.'}
      </p>
    </motion.div>
  );
};

// Main component
const Card = () => {
  const navigate = useNavigate();
  const [currentCompanyIndex, setCurrentCompanyIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [shouldAutoSlide, setShouldAutoSlide] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [isNavigating, setIsNavigating] = useState(false); // New state for navigation loading
  const [companies, setCompanies] = useState([]);
  const [products, setProducts] = useState([]);
  // const autoSlideTimerRef = useRef(null);

  // Fetch companies on initial load
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        setIsLoading(true);
        const response = await axios.get(`${process.env.REACT_APP_API_URI}/brand/company/${65}`);
        if (response.status === 200) {
          setCompanies(response.data);
          // Once companies are loaded, fetch products for the first company
          if (response.data.length > 0) {
            fetchProductsByCompany(response.data[0].brand_id);
          } else {
            setIsLoading(false);
          }
        }
      } catch (error) {
        console.error("Error fetching companies:", error);
        setIsLoading(false);
      }
    };

    fetchCompanies();
  }, []);

  // Function to fetch products by company ID
  const fetchProductsByCompany = async (companyId) => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${process.env.REACT_APP_API_URI}/products/brand-product/${companyId}`);
      if (response.status === 200) {
        setProducts(response.data);
      }
    } catch (error) {
      console.error(`Error fetching products for company ID ${companyId}:`, error);
      setProducts([]); // Set empty array on error
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  const goToCompany = useCallback((index) => {
    if (isAnimating || companies.length === 0) return;
    setIsAnimating(true);
    setCurrentCompanyIndex(index);
    setShouldAutoSlide(true);

    // Fetch products for the selected company
    fetchProductsByCompany(companies[index].brand_id);

    requestAnimationFrame(() => {
      setTimeout(() => setIsAnimating(false), 600);
    });
  }, [isAnimating, companies]);

  const nextSlide = useCallback(() => {
    if (companies.length === 0) return;
    const nextIndex = currentCompanyIndex < companies.length - 1 ? currentCompanyIndex + 1 : 0;
    goToCompany(nextIndex);
  }, [currentCompanyIndex, companies.length, goToCompany]);

  const prevSlide = useCallback(() => {
    if (companies.length === 0) return;
    const prevIndex = currentCompanyIndex > 0 ? currentCompanyIndex - 1 : companies.length - 1;
    goToCompany(prevIndex);
  }, [currentCompanyIndex, companies.length, goToCompany]);

  const handleShopNow = useCallback(async (brandId, productId) => {
    try {
      setIsNavigating(true); // Use navigation loader instead of regular loading

      // Scroll to top before making the API call
      window.scrollTo({ top: 0, behavior: 'smooth' });

      // Get company name from our companies state
      const company = companies.find(c => c.brand_id === brandId)?.brand_name || "";

      const response = await axios.get(`${process.env.REACT_APP_API_URI}/products/brand/${brandId}`);

      if (response.status === 200) {
        localStorage.setItem('apiProducts', JSON.stringify(response.data));

        navigate(`/product?brand=${company}&id=${brandId}`, {
          state: {
            apiData: response.data,
            brandId: brandId
          }
        });
      }
    } catch (error) {
      console.error("Error fetching company products:", error);
      setIsNavigating(false); // Reset navigating state on error
    }
    // No finally block here since we're navigating away
  }, [navigate, companies]);

  // const handleInteraction = () => {
  //   setShouldAutoSlide(false);
  //   clearTimeout(autoSlideTimerRef.current);
  //   autoSlideTimerRef.current = setTimeout(() => {
  //     setShouldAutoSlide(true);
  //   }, 15000);
  // };

  // useEffect(() => {
  //   if (!shouldAutoSlide || companies.length === 0) return;

  //   const interval = setInterval(() => {
  //     if (!isAnimating) {
  //       nextSlide();
  //     }
  //   }, 6000);

  //   return () => clearInterval(interval);
  // }, [currentCompanyIndex, isAnimating, nextSlide, shouldAutoSlide, companies.length]);

  // useEffect(() => {
  //   return () => {
  //     clearTimeout(autoSlideTimerRef.current);
  //   };
  // }, []);

  // Get current company name safely
  const currentCompanyName = companies.length > 0 && currentCompanyIndex < companies.length
    ? companies[currentCompanyIndex].brand_name
    : "";

  return (
    <div
      className="relative min-h-screen bg-gray-50 text-gray-800 overflow-hidden"
    // onClick={handleInteraction}
    // onMouseMove={handleInteraction}
    >
      {/* Use the original Loader only for navigation to product page */}
      {isNavigating && <Loader Logo={Logo} />}

      {/* Use the new CardsLoader for company/products loading */}
      {isLoading && !isNavigating && <CardsLoader />}

      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-[#f36812] to-[#ff9259]"></div>
        <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-[#f36812]/5 blur-3xl"></div>
        <div className="absolute -bottom-40 -left-20 w-96 h-96 rounded-full bg-[#f36812]/5 blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-12 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8 md:mb-12"
        >
          <div className="flex items-center justify-center mb-2">
            <FaCrown className="text-[#f36812] w-5 h-5 mr-2" />
            <h2 className="text-sm md:text-base uppercase tracking-widest text-[#f36812] font-semibold">
              VIP Collection
            </h2>
          </div>

          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 tracking-tight">
            {currentCompanyName}
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#f36812] to-[#ff9259]"> Premium</span>
          </h1>

          <div className="w-full max-w-md mx-auto mt-3">
            <div className="h-px bg-gradient-to-r from-transparent via-[#f36812]/40 to-transparent"></div>
          </div>

          <p className="mt-3 md:mt-4 text-base md:text-lg text-gray-600 font-light max-w-2xl mx-auto">
            Exclusive luxury products crafted for the most discerning customers who demand nothing but excellence.
          </p>
        </motion.div>

        {companies.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="hidden md:flex justify-center gap-3 mb-10 flex-wrap"
          >
            {companies.map((company, index) => (
              <button
                key={company.brand_id}
                onClick={() => goToCompany(index)}
                className={`px-5 py-2 rounded-full transition-all duration-200 text-sm font-medium
                  ${currentCompanyIndex === index
                    ? 'bg-[#f36812] text-white shadow-md shadow-[#f36812]/20'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              >
                {company.brand_name}
              </button>
            ))}
          </motion.div>
        )}

        <div className="relative px-0 sm:px-4 md:px-6">
          <AnimatePresence mode="wait">
            {!isLoading && products.length > 0 ? (
              <motion.div
                key={currentCompanyName}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.5 }}
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6"
              >
                {products.slice(0, isMobile ? 2 : 4).map((product) => (
                  <ProductCard
                    key={product.product_id}
                    product={product}
                    isMobile={isMobile}
                    onShopNow={handleShopNow}
                  />
                ))}
              </motion.div>
            ) : !isLoading ? (
              <NoProductsFound companyName={currentCompanyName} />
            ) : null}
          </AnimatePresence>

          <motion.button
            onClick={prevSlide}
            disabled={isAnimating || companies.length === 0}
            className="absolute -left-2 md:-left-4 top-1/2 -translate-y-1/2 w-10 h-10 md:w-11 md:h-11 rounded-full bg-white shadow-md border border-gray-200 text-gray-700 hover:bg-[#f36812] hover:text-white flex items-center justify-center transition-all duration-200 disabled:opacity-50 z-10"
            whileHover={{ scale: isAnimating ? 1 : 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <FaAngleLeft className="w-5 h-5" />
          </motion.button>

          <motion.button
            onClick={nextSlide}
            disabled={isAnimating || companies.length === 0}
            className="absolute -right-2 md:-right-4 top-1/2 -translate-y-1/2 w-10 h-10 md:w-11 md:h-11 rounded-full bg-white shadow-md border border-gray-200 text-gray-700 hover:bg-[#f36812] hover:text-white flex items-center justify-center transition-all duration-200 disabled:opacity-50 z-10"
            whileHover={{ scale: isAnimating ? 1 : 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <FaAngleRight className="w-5 h-5" />
          </motion.button>
        </div>

        {companies.length > 0 && (
          <div className="mt-8 md:mt-10 flex justify-center items-center gap-2">
            {companies.map((company, index) => (
              <button
                key={company.brand_id}
                onClick={() => goToCompany(index)}
                className="group focus:outline-none"
                aria-label={`Go to ${company.brand_name} collection`}
              >
                <div className={`h-1.5 rounded-full transition-all duration-300 ${currentCompanyIndex === index
                    ? 'w-8 bg-[#f36812]'
                    : 'w-1.5 bg-gray-300 group-hover:bg-gray-400'
                  }`}></div>
              </button>
            ))}
          </div>
        )}

        {companies.length > 0 && (
          <div className="md:hidden mt-5 text-center">
            <div className="inline-block px-4 py-1.5 rounded-full bg-gray-100 text-sm font-medium text-[#f36812]">
              {currentCompanyName}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Card;